import { Simplify } from 'type-fest';

import { DeviceTrackHistoryResponse, TrackItem } from '@/api/device/types';
import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import {
    CombinedQueriesResult,
    CombinedQueryOrQueriesResult,
    CombinedQueryResult,
    QueryConfig,
    SpotterIdOrIds,
} from '@/common/react-query';
import { DEVICE_TRACK_HISTORY_QUERY_KEY } from '@/config';
import { DateRange } from '@/store';

export type GetDeviceTrackHistoryParams = {
    spotterId: string;
    startDate: string;
    endDate: string;
};

export const getDeviceTrackHistory = async (
    params: GetDeviceTrackHistoryParams,
    options?: RequestOptions
): Promise<TrackItem[]> => {
    const response = await axios.post<DeviceTrackHistoryResponse, DeviceTrackHistoryResponse>(
        '/fetch/track-history',
        params,
        {
            params: { processingSources: 'all' },
            ...requestConfig(options),
        }
    );
    /*
     * `forecastLocation` and `velocity` come back alongside the samples but are derived from the newest
     * two of them, so they describe the window rather than any one item and cannot survive being split in
     * two. Nothing consumes them today; whatever does should take the live half's.
     */
    return response.data.trackHistory.track;
};

export type UseDeviceTrackHistoryOptions = {
    dateRange?: DateRange | null;
    config?: Omit<QueryConfig<typeof getDeviceTrackHistory>, 'select'>;
} & SpotterIdOrIds;

export function useDeviceTrackHistory(
    options: Simplify<Omit<UseDeviceTrackHistoryOptions, 'spotterIds'>>
): CombinedQueryResult<TrackItem[]>;

export function useDeviceTrackHistory(
    options: Simplify<Omit<UseDeviceTrackHistoryOptions, 'spotterId'>>
): CombinedQueriesResult<TrackItem[]>;

export function useDeviceTrackHistory({
    spotterId,
    spotterIds,
    dateRange,
    config,
}: UseDeviceTrackHistoryOptions): CombinedQueryOrQueriesResult<TrackItem[]> {
    return useLiveSplitQuery({
        queryKeyPrefix: DEVICE_TRACK_HISTORY_QUERY_KEY,
        spotterId,
        spotterIds,
        dateRange,
        fetchRange: ({ viewToken, signal, ...params }) => getDeviceTrackHistory(params, { viewToken, signal }),
        // one of the older Spotter entrypoints, so it excludes the bounds it is given
        apiDateBounds: 'exclusive',
        timestampKey: 'timestamp',
        /**
         * The track-history endpoint returns desc order when called with both start/end date.
         * Oceans api is the opposite but `useDeviceTrack` sorts the oceans-api response to match desc order.
         */
        sortOrder: 'desc',
        config,
    });
}
